import React from 'react';

interface IBCVisualizationProps {
  levelPercent: number; // 0-100
  thresholdPercent?: number; // 0-100, optional threshold line
  currentLiters?: number; // Current volume in liters
  thresholdLiters?: number; // Threshold volume in liters
  maxCapacityLiters?: number; // Maximum capacity in liters, default 1000
  height?: number; // Height in pixels, default 300
  width?: number; // Width in pixels, default 200
  className?: string;
  hideScales?: boolean;
}

export default function IBCVisualization({
  levelPercent,
  thresholdPercent,
  currentLiters,
  thresholdLiters,
  maxCapacityLiters = 1000,
  height = 300,
  width = 300,
  className = '',
  hideScales = false,
}: IBCVisualizationProps) {
  // Clamp values between 0 and 100
  const clampedLevel = Math.max(0, Math.min(100, levelPercent));
  const clampedThreshold = thresholdPercent
    ? Math.max(0, Math.min(100, thresholdPercent))
    : undefined;

  // Calculate dimensions for the IBC container
  const containerMargin = 20;
  const containerWidth = width - containerMargin * 2;
  const containerHeight = height - 40; // Space for top/bottom
  const containerTop = 20;
  const containerLeft = containerMargin;

  // Calculate fill height (from bottom)
  const fillHeight = (clampedLevel / 100) * containerHeight;
  const thresholdY = clampedThreshold
    ? containerTop + containerHeight - (clampedThreshold / 100) * containerHeight
    : undefined;

  // IBC specific dimensions
  const frameThickness = 8;
  const valveSize = 12;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="relative" style={{ width, height }}>
        <svg width={width} height={height} className="overflow-visible">
          {/* IBC Container outer frame (metal cage) */}
          <rect
            x={containerLeft - frameThickness}
            y={containerTop - frameThickness}
            width={containerWidth + frameThickness * 2}
            height={containerHeight + frameThickness * 2}
            fill="none"
            stroke="#6B7280"
            strokeWidth="3"
            rx="4"
          />

          {/* IBC Container inner tank */}
          <rect
            x={containerLeft}
            y={containerTop}
            width={containerWidth}
            height={containerHeight}
            fill="#F9FAFB"
            stroke="#374151"
            strokeWidth="2"
            rx="2"
          />

          {/* Liquid fill */}
          {fillHeight > 0 && (
            <rect
              x={containerLeft + 2}
              y={containerTop + containerHeight - fillHeight}
              width={containerWidth - 4}
              height={fillHeight}
              fill="#3B82F6"
              fillOpacity="0.7"
              className="liquid-fill"
            />
          )}

          {/* Liquid surface (slight gradient for realism) */}
          {fillHeight > 0 && (
            <defs>
              <linearGradient id="liquidSurface" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2563EB" stopOpacity="0.9" />
                <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#2563EB" stopOpacity="0.9" />
              </linearGradient>
            </defs>
          )}
          {fillHeight > 0 && (
            <rect
              x={containerLeft + 2}
              y={containerTop + containerHeight - fillHeight}
              width={containerWidth - 4}
              height="4"
              fill="url(#liquidSurface)"
            />
          )}

          {/* Threshold line */}
          {thresholdY !== undefined && (
            <line
              x1={containerLeft - 5}
              y1={thresholdY}
              x2={containerLeft + containerWidth + 5}
              y2={thresholdY}
              stroke="#EF4444"
              strokeWidth="2"
              strokeDasharray="5,5"
              className="threshold-line"
            />
          )}

          {/* IBC Frame details - vertical supports */}
          <line
            x1={containerLeft + containerWidth / 4 - frameThickness / 2}
            y1={containerTop - frameThickness}
            x2={containerLeft + containerWidth / 4 - frameThickness / 2}
            y2={containerTop + containerHeight + frameThickness}
            stroke="#6B7280"
            strokeWidth="2"
          />
          <line
            x1={containerLeft + containerWidth / 1.3 + frameThickness / 2}
            y1={containerTop - frameThickness}
            x2={containerLeft + containerWidth / 1.3 + frameThickness / 2}
            y2={containerTop + containerHeight + frameThickness}
            stroke="#6B7280"
            strokeWidth="2"
          />

          {/* IBC Frame details - horizontal supports */}
          <line
            x1={containerLeft - frameThickness}
            y1={containerTop + containerHeight / 3}
            x2={containerLeft + containerWidth + frameThickness}
            y2={containerTop + containerHeight / 3}
            stroke="#6B7280"
            strokeWidth="2"
          />
          <line
            x1={containerLeft - frameThickness}
            y1={containerTop + (2 * containerHeight) / 3}
            x2={containerLeft + containerWidth + frameThickness}
            y2={containerTop + (2 * containerHeight) / 3}
            stroke="#6B7280"
            strokeWidth="2"
          />

          {/* Bottom valve */}
          <rect
            x={containerLeft + containerWidth / 2 - valveSize / 2}
            y={containerTop + containerHeight}
            width={valveSize}
            height={valveSize}
            fill="#374151"
            stroke="#1F2937"
            strokeWidth="1"
            rx="2"
          />

          {/* Top cap/lid */}
          <rect
            x={containerLeft + containerWidth / 2 - 15}
            y={containerTop - 8}
            width="30"
            height="8"
            fill="#6B7280"
            stroke="#374151"
            strokeWidth="1"
            rx="2"
          />
        </svg>

        {/* Percentage labels */}
        {!hideScales && (
          <div className="absolute -right-8 top-0 h-full flex flex-col justify-between text-xs text-gray-500">
            <div className="text-right">
              <div>{maxCapacityLiters}L</div>
              <div className="text-gray-400">100%</div>
            </div>
            <div className="text-right">
              <div>{Math.round(maxCapacityLiters * 0.75)}L</div>
              <div className="text-gray-400">75%</div>
            </div>
            <div className="text-right">
              <div>{Math.round(maxCapacityLiters * 0.5)}L</div>
              <div className="text-gray-400">50%</div>
            </div>
            <div className="text-right">
              <div>{Math.round(maxCapacityLiters * 0.25)}L</div>
              <div className="text-gray-400">25%</div>
            </div>
            <div className="text-right">
              <div>0L</div>
              <div className="text-gray-400">0%</div>
            </div>
          </div>
        )}

        {/* Current level indicator */}
        {!hideScales && (
          <div
            className="absolute -left-8 text-sm font-semibold text-blue-600"
            style={{ top: `${containerTop + containerHeight - fillHeight - 10}px` }}
          >
            <div className="text-right">
              {currentLiters !== undefined && <div>{currentLiters.toFixed(0)}L</div>}
              <div className="text-xs font-normal">{clampedLevel.toFixed(0)}%</div>
            </div>
          </div>
        )}

        {/* Threshold indicator */}
        {!hideScales && thresholdY !== undefined && (
          <div
            className="absolute -right-32 text-xs font-medium text-red-500"
            style={{ top: `${thresholdY - 8}px` }}
          >
            <div className="text-right">
              <div>Grænseværdi: {clampedThreshold}%</div>
              {thresholdLiters !== undefined && (
                <div className="text-red-400">{thresholdLiters.toFixed(0)}L</div>
              )}
            </div>
          </div>
        )}

        {/* Volume indicator */}
        {!hideScales && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 translate-y-8 text-xs text-gray-600 font-medium text-center">
            <div>
              {currentLiters !== undefined
                ? currentLiters.toFixed(0)
                : Math.round((clampedLevel / 100) * maxCapacityLiters)}
              L / {maxCapacityLiters}L
            </div>
            {/* <div className="text-gray-400 mt-1">{clampedLevel.toFixed(1)}%</div> */}
          </div>
        )}
      </div>

      {/* Legend */}
      {!hideScales && (
        <div className="mt-6 flex items-center space-x-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-blue-500 bg-opacity-70 rounded"></div>
            <span>Niveau</span>
          </div>
          {thresholdPercent !== undefined && (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-1 bg-red-500" style={{ borderTop: '2px dashed #EF4444' }}></div>
              <span>Grænseværdi</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
